import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { ArrowPathIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ReactComponent as CelebrateIcon } from '../../assets/celebrate.svg';
import { PiSmileyMeltingBold } from "react-icons/pi";
import { FaArrowUp } from "react-icons/fa6";
import ChatInput from "./components/ChatInput";
import ChatMessages from "./components/ChatMessages";
import ArtifactPanel from "./components/ArtifactPanel";
import ChatLengthWarning from "./components/ChatLengthWarning";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMessageHandling } from './hooks/useMessageHandling';
import { useMessagePagination } from './hooks/useMessagePagination';
import { useFileUpload } from './hooks/useFileUpload';
import { useChatLength } from './hooks/useChatLength';
import { useToolCalls } from './hooks/useToolCalls';
import { GreetingComponent as BaseGreetingComponent } from './components/GreetingComponent';
import { GreetingComponentV2 as BaseGreetingComponentV2 } from './components/GreetingComponentV2';
import { GreetingComponentV3 as BaseGreetingComponentV3 } from './components/GreetingComponentV3';
import { getEnhancedComponents } from '../../utils/componentFactory';
import { useTheme } from '../../features/theme/context/ThemeContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getCachedChatTitle } from '../../utils/chatTitleCache';
import axios from 'axios';
import websocketService from '../../utils/websocketService';
import NvgtrShimmerLoader from "./components/NvgtrShimmerLoader";
// import TestUnifiedLoading from './components/TestUnifiedLoading';

// Get enhanced components for development
const enhancedComponents = getEnhancedComponents({
  GreetingComponent: BaseGreetingComponent,
  GreetingComponentV2: BaseGreetingComponentV2,
  GreetingComponentV3: BaseGreetingComponentV3
});
const { GreetingComponentV3 } = enhancedComponents;

const ARTIFACT_PANEL_STORAGE_KEY = 'artifactPanelState';

const isPreviewableArtifact = (generatedFile) => {
  const format = generatedFile?.format?.toLowerCase?.() || '';
  const fileName = generatedFile?.fileName?.toLowerCase?.() || '';
  if (format === 'html' || format === 'pdf') return true;
  return fileName.endsWith('.html') || fileName.endsWith('.pdf');
};

const isPptxCodePreviewMessage = (message) => {
  if (!message?._codePreview || message?.generatedFile) return false;
  const explicitTool = (message?._activeFileTool || '').toLowerCase();
  if (explicitTool.includes('powerpoint') || explicitTool.includes('pptx')) return true;

  const toolNames = (message?.toolCallPositions || [])
    .map((pos) => (pos?.functionName || '').toLowerCase())
    .filter(Boolean);

  return toolNames.some((name) => name.includes('powerpoint') || name.includes('pptx'));
};

/** Find artifact (messageId + generatedFile) in messages - checks top-level and nested responses */
function findArtifactInMessages(messages, messageId, chatId) {
  if (!messages?.length || !messageId || !chatId) return null;
  for (const msg of messages) {
    if (msg._id !== messageId) continue;
    if (isPreviewableArtifact(msg.generatedFile)) {
      return { messageId: msg._id, chatId, generatedFile: msg.generatedFile };
    }
    if (msg.responses) {
      for (const r of msg.responses) {
        if (isPreviewableArtifact(r.generatedFile)) {
          return { messageId: msg._id, chatId, generatedFile: r.generatedFile };
        }
      }
    }
    return null;
  }
  return null;
}

// Component for displaying chat not found error
const ChatNotFoundError = ({ darkMode, onNewChat }) => (
  <div className="absolute inset-0 flex items-center justify-center p-8">
    <div className="text-center max-w-md">
      <div className="mb-6">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
          darkMode ? 'bg-red-500/50 text-red-300' : 'bg-red-100 text-red-500'
        }`}>
          <PiSmileyMeltingBold className="w-8 h-8" />
        </div>
        <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Uh oh. This chat couldn't be found.
        </h3>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-[#999999]'}`}>
          The chat you're looking for may have been deleted or moved.
        </p>
      </div>
      <button
        onClick={onNewChat}
        className={`px-5 py-2.5 text-md rounded-xl font-medium transition-all duration-200 ${
          darkMode 
            ? 'bg-black dark:bg-white hover:bg-[#282828] dark:hover:bg-[#c3c3c3] text-white dark:text-black' 
            : 'bg-black dark:bg-white hover:bg-[#282828] dark:hover:bg-[#c3c3c3] text-white dark:text-black'
        } shadow-sm hover:shadow-md`}
      >
        Create a New Chat
      </button>
    </div>
  </div>
);

function parseDefaultChatModelKey(key) {
  if (typeof key !== 'string' || !key.includes(':')) return null;
  const i = key.indexOf(':');
  return { provider: key.slice(0, i), id: key.slice(i + 1) };
}

const Chat = forwardRef(({ user, setUser, onMessagesUpdate, sidebarRef, onModelSelectionChange, onArtifactPanelChange, artifactPanelLifted = false }, ref) => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const location = useLocation();
  const [input, setInput] = useState("");
  const [showGreeting, setShowGreeting] = useState(!chatId);
  const [showFeedbackNotification, setShowFeedbackNotification] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [chatInputHeight, setChatInputHeight] = useState(130);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  
  // User join notification state
  const [joinNotifications, setJoinNotifications] = useState([]);
  
  // Typing indicator state
  const [typingUsers, setTypingUsers] = useState(new Map()); // Map of userId -> { name, color, timestamp }
  
  const chatContainerRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);
  const joinNotificationTimeoutRef = useRef(new Map()); // Map of notificationId -> timeout
  const typingTimeoutRef = useRef(new Map()); // Map of userId -> timeout
  const { darkMode } = useTheme();

  // Tool call manager
  const toolCallManager = useToolCalls();

  // Document title management
  useDocumentTitle(chatId);

  // Direct search state
  const [searchState, setSearchState] = useState(null);

  // Artifact panel - slides out from right when chart/file artifact exists
  // Set to true to force panel open for design (chat + input slide over)
  const FORCE_ARTIFACT_PANEL = false;
  const mockArtifact = useMemo(() => ({
    messageId: 'design-mode',
    chatId: chatId || 'design',
    generatedFile: { format: 'html', fileName: 'chart.html', fileSize: 1024 }
  }), [chatId]);

  const [activeArtifact, setActiveArtifact] = useState(null);
  const [isArtifactPanelOpen, setIsArtifactPanelOpen] = useState(false);

  // Force panel open in dev for design
  useEffect(() => {
    if (FORCE_ARTIFACT_PANEL) {
      setActiveArtifact(mockArtifact);
      setIsArtifactPanelOpen(true);
    }
  }, [FORCE_ARTIFACT_PANEL, mockArtifact]);

  const openArtifactPanel = useCallback((artifact) => {
    setActiveArtifact(artifact);
    setIsArtifactPanelOpen(true);
  }, []);

  const closeArtifactPanel = useCallback(() => {
    setIsArtifactPanelOpen(false);
  }, []);

  // Sync artifact state to parent (MainInterface) for lifted layout + persist to localStorage
  useEffect(() => {
    if (!onArtifactPanelChange) return;
    onArtifactPanelChange({
      isOpen: isArtifactPanelOpen,
      artifact: activeArtifact,
      chatId,
      onClose: closeArtifactPanel,
    });
    try {
      localStorage.setItem(ARTIFACT_PANEL_STORAGE_KEY, JSON.stringify({
        chatId: chatId || null,
        messageId: activeArtifact?.messageId ?? null,
        isOpen: isArtifactPanelOpen,
      }));
    } catch (e) { /* ignore */ }
  }, [isArtifactPanelOpen, activeArtifact, chatId, closeArtifactPanel, onArtifactPanelChange]);

  // Clear artifact when switching chats (unless design mode)
  useEffect(() => {
    if (!FORCE_ARTIFACT_PANEL) {
      setActiveArtifact(null);
      setIsArtifactPanelOpen(false);
    } else {
      setActiveArtifact(mockArtifact);
    }
  }, [chatId, FORCE_ARTIFACT_PANEL, mockArtifact]);

  const hasRestoredArtifactRef = useRef(false);
  const hasInitializedPdfAutoOpenRef = useRef(false);
  const lastAutoOpenedPdfKeyRef = useRef(null);
  useEffect(() => {
    if (!chatId) hasRestoredArtifactRef.current = false;
  }, [chatId]);

  useEffect(() => {
    hasInitializedPdfAutoOpenRef.current = false;
    lastAutoOpenedPdfKeyRef.current = null;
  }, [chatId]);

  const chatAreaRef = useRef(null);
  const [chatAreaBounds, setChatAreaBounds] = useState(null);

  // Update chat area bounds for feedback notification positioning (centered over chat, above header)
  useEffect(() => {
    if (!showFeedbackNotification || !chatAreaRef.current) {
      setChatAreaBounds(null);
      return;
    }
    const updateBounds = () => {
      if (chatAreaRef.current) {
        const rect = chatAreaRef.current.getBoundingClientRect();
        setChatAreaBounds({ left: rect.left, width: rect.width });
      }
    };
    updateBounds();
    const ro = new ResizeObserver(updateBounds);
    ro.observe(chatAreaRef.current);
    window.addEventListener('resize', updateBounds);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateBounds);
    };
  }, [showFeedbackNotification]);

  // Check if this is a project chat or client chat by looking at URL parameters
  const urlParams = new URLSearchParams(location.search);
  const projectId = urlParams.get('project');
  const clientId = urlParams.get('client');
  const isProjectChat = !!projectId;
  const isClientChat = !!clientId;

  // Get current typing user (if any) for display
  const currentTypingUser = Array.from(typingUsers.values())[0]; // Show only the first typing user
  
  // Debug logging for typing state
  useEffect(() => {
    console.log('Chat.js typing state changed:', {
      typingUsersSize: typingUsers.size,
      currentTypingUser: currentTypingUser,
      isProjectChat: isProjectChat,
      currentChatId: chatId
    });
  }, [typingUsers, currentTypingUser, isProjectChat, chatId]);



  // Add CSS for webkit scrollbar behavior
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .chat-container::-webkit-scrollbar {
        width: 0px;
        background: transparent;
      }
      .chat-container:hover::-webkit-scrollbar {
        width: 8px;
      }
      .chat-container:hover::-webkit-scrollbar-track {
        background: transparent;
      }
      .chat-container:hover::-webkit-scrollbar-thumb {
        background-color: ${darkMode ? '#525252' : '#E5E7EB'} !important;
        border-radius: 4px;
      }
      .chat-container:hover::-webkit-scrollbar-thumb:hover {
        background-color: ${darkMode ? '#737373' : '#E5E7EB'} !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [darkMode]);

  // Clean up stale typing indicators
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 60000; // 1 minute timeout to match ChatInput
      
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        let hasChanges = false;
        
        for (const [userId, userData] of newMap.entries()) {
          if (now - userData.timestamp > staleThreshold) {
            newMap.delete(userId);
            hasChanges = true;
            
            // Clear timeout for this user
            if (typingTimeoutRef.current.has(userId)) {
              clearTimeout(typingTimeoutRef.current.get(userId));
              typingTimeoutRef.current.delete(userId);
            }
          }
        }
        
        return hasChanges ? newMap : prev;
      });
    }, 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Fetch project data if this is a project chat
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) {
        setProjectData(null);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setProjectData(response.data.project);
      } catch (error) {
        console.error('Error fetching project data:', error);
        setProjectData(null);
      }
    };

    fetchProjectData();
  }, [projectId]);

  // Fetch active users for project chats - SIMPLIFIED AND RELIABLE
  useEffect(() => {
    if (!isProjectChat || !chatId) {
      setActiveUsers([]);
      return;
    }

    // Initial fetch only - no polling, no intervals
    const fetchInitialActiveUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/${chatId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('🔴 Initial active users loaded:', response.data.activeUsers);
        setActiveUsers(response.data.activeUsers || []);
      } catch (error) {
        console.error('Error fetching initial active users:', error);
        setActiveUsers([]);
      }
    };

    fetchInitialActiveUsers();
  }, [chatId, isProjectChat]);

  // CONSOLIDATED WebSocket listener for ALL chat updates (join/leave/typing) - LIKE TYPING SYSTEM
  useEffect(() => {
    if (!chatId || !isProjectChat) return;

    const handleWebSocketMessage = (data) => {
      if (data.type === 'chat_update' && data.chatId === chatId) {
        const updateType = data.data?.type;
        
        // IMMEDIATE active users updates from WebSocket - NO API CALLS
        if (updateType === 'user_joined_chat') {
          const { userId, userName, userColor, activeUsers: wsActiveUsers } = data.data;
          
          console.log(`🎉 INSTANT JOIN: ${userName}`);
          
          // Use WebSocket data if available, otherwise add manually
          if (wsActiveUsers && Array.isArray(wsActiveUsers)) {
            setActiveUsers(wsActiveUsers);
            console.log('🔴 Active users from WebSocket:', wsActiveUsers);
          } else {
            // Fallback: add user manually (like typing system)
            setActiveUsers(prev => {
              const userExists = prev.some(u => u.userId === userId);
              if (!userExists) {
                return [...prev, {
                  userId,
                  userName,
                  userColor,
                  lastActivity: Date.now(),
                  profileImage: null // Will be enriched by backend next time
                }];
              }
              return prev;
            });
          }
          
          // Join notification (don't show for self)
          if (userId !== user?.id) {
            const notificationId = Date.now() + Math.random();
            const notification = {
              id: notificationId,
              firstName: userName.split(' ')[0],
              userColor: userColor,
              timestamp: Date.now()
            };
            
            setJoinNotifications(prev => [...prev, notification]);
            
            // Auto-remove after 4 seconds
            const timeout = setTimeout(() => {
              setJoinNotifications(prev => prev.filter(n => n.id !== notificationId));
              joinNotificationTimeoutRef.current.delete(notificationId);
            }, 4000);
            
            joinNotificationTimeoutRef.current.set(notificationId, timeout);
          }
        }
        
        if (updateType === 'user_left_chat') {
          const { userId, userName, activeUsers: wsActiveUsers } = data.data;
          
          console.log(`👋 INSTANT LEAVE: ${userName}`);
          
          // Use WebSocket data if available, otherwise remove manually
          if (wsActiveUsers && Array.isArray(wsActiveUsers)) {
            setActiveUsers(wsActiveUsers);
            console.log('🔴 Active users from WebSocket:', wsActiveUsers);
          } else {
            // Fallback: remove user manually (like typing system)
            setActiveUsers(prev => prev.filter(u => u.userId !== userId));
          }
        }
      }
    };

    // SINGLE LISTENER - no conflicts
    const listenerId = `chat-updates-${chatId}`;
    console.log(`👂 Setting up consolidated WebSocket listener: ${listenerId}`);
    websocketService.addListener(listenerId, handleWebSocketMessage);

    return () => {
      console.log(`🧹 Cleaning up consolidated WebSocket listener: ${listenerId}`);
      websocketService.removeListener(listenerId);
      
      // Clean up join notification timeouts
      joinNotificationTimeoutRef.current.forEach(timeout => {
        clearTimeout(timeout);
      });
      joinNotificationTimeoutRef.current.clear();
    };
  }, [chatId, isProjectChat, user?.id]);

  // Manual close notification
  const closeJoinNotification = useCallback((notificationId) => {
    setJoinNotifications(prev => prev.filter(n => n.id !== notificationId));
    
    // Clear timeout
    if (joinNotificationTimeoutRef.current.has(notificationId)) {
      clearTimeout(joinNotificationTimeoutRef.current.get(notificationId));
      joinNotificationTimeoutRef.current.delete(notificationId);
    }
  }, []);

  // Simple scroll management
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Check scroll position to show/hide gradient (fixed for reliable detection)
  const checkScrollPosition = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Check if content is actually scrollable (content height > container height)
    const isScrollable = scrollHeight > clientHeight;
    
    // If not scrollable, NEVER show gradient
    if (!isScrollable) {
      setCanScrollUp(false);
      return;
    }
    
    // More robust threshold detection - account for floating point precision
    const threshold = 10; // Increased threshold for better reliability
    const isAtTop = scrollTop <= threshold;
    
    // Calculate the new state
    const shouldShowGradient = Boolean(!isAtTop && isScrollable);
    
    // Only update state if it actually changed to prevent unnecessary re-renders
    setCanScrollUp(prev => prev !== shouldShowGradient ? shouldShowGradient : prev);
  }, []);
  
  // Handle chat created callback
  const handleChatCreated = useCallback((newChatId) => {
    console.log('New chat created via WebSocket:', newChatId);
    setShowGreeting(false);
    
    // Navigate to the new chat URL if we're currently at the root chat page
    if (!chatId && newChatId) {
      let newUrl;
      if (isProjectChat) {
        newUrl = `/chat/${newChatId}?project=${projectId}`;
      } else if (isClientChat) {
        newUrl = `/chat/${newChatId}?client=${clientId}`;
      } else {
        newUrl = `/chat/${newChatId}`;
      }
      console.log('Navigating to new chat:', newUrl);
      navigate(newUrl);
      
      // Notify sidebar about chat creation
      if (isProjectChat) {
        // For project chats, notify the project dashboard
        window.dispatchEvent(new CustomEvent('projectChatCreated', {
          detail: { 
            chatId: newChatId,
            projectId: projectId
          }
        }));
      } else {
        // For personal and client chats, notify the sidebar
        window.dispatchEvent(new CustomEvent('newChatCreated', {
          detail: { chatId: newChatId, clientId: clientId }
        }));
      }
    }
  }, [chatId, navigate, isProjectChat, projectId, isClientChat, clientId]);

  // Handle typing events from WebSocket
  const handleTypingEvent = useCallback((typingData) => {
    const { userId, userName, userColor, isTyping, timestamp } = typingData;
    
    // Don't show our own typing indicator
    if (userId === user?.id) return;
    
    console.log(`Typing event: ${userName} ${isTyping ? 'started' : 'stopped'} typing`);
    
    if (isTyping) {
      // User started typing or continued typing - always reset the timeout
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, {
          name: userName,
          color: userColor,
          timestamp: timestamp || Date.now()
        });
        
        // Dispatch event to notify MainInterface that typing state changed
        window.dispatchEvent(new CustomEvent('typing-state-changed', {
          detail: {
            chatId: chatId,
            typingUsers: newMap
          }
        }));
        
        return newMap;
      });
      
      // Clear any existing timeout for this user (important for resetting on continued typing)
      if (typingTimeoutRef.current.has(userId)) {
        clearTimeout(typingTimeoutRef.current.get(userId));
        typingTimeoutRef.current.delete(userId);
      }
      
      // Set new timeout to remove typing indicator after 1 minute
      const timeout = setTimeout(() => {
        setTypingUsers(prev => {
          const newMap = new Map(prev);
          newMap.delete(userId);
          
          // Dispatch event when user stops typing due to timeout
          window.dispatchEvent(new CustomEvent('typing-state-changed', {
            detail: {
              chatId: chatId,
              typingUsers: newMap
            }
          }));
          
          return newMap;
        });
        typingTimeoutRef.current.delete(userId);
      }, 60000); // 1 minute timeout to match ChatInput
      
      typingTimeoutRef.current.set(userId, timeout);
    } else {
      // User stopped typing
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        
        // Dispatch event to notify MainInterface that typing state changed
        window.dispatchEvent(new CustomEvent('typing-state-changed', {
          detail: {
            chatId: chatId,
            typingUsers: newMap
          }
        }));
        
        return newMap;
      });
      
      // Clear timeout for this user
      if (typingTimeoutRef.current.has(userId)) {
        clearTimeout(typingTimeoutRef.current.get(userId));
        typingTimeoutRef.current.delete(userId);
      }
    }
  }, [user?.id, chatId]);

  // Helper function to get user color (same logic as backend)
  const getUserColor = useCallback((userId) => {
    const pastelColors = [
      '#F5C8BD', '#E9BACC', '#D0AAB0', '#B599C1', '#ABACCB', '#9FBFDD',
      '#97CCDC', '#83C4B4', '#A9D194', '#D5E4AD', '#FAEEAE', '#F5BE6B'
    ];
    
    if (!userId) return pastelColors[0];
    
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return pastelColors[Math.abs(hash) % pastelColors.length];
  }, []);

  // Join chat session when component mounts or chatId changes
  useEffect(() => {
    if (!chatId || !user?.id || !isProjectChat) {
      console.log('Skipping join_chat - conditions not met:', {
        chatId: !!chatId,
        userId: !!user?.id,
        isProjectChat
      });
      return;
    }

    const userData = {
      userName: user.displayName || 
               (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
               user.name || 
               user.firstName || 
               user.email?.split('@')[0] || 
               'Unknown User',
      userColor: getUserColor(user.id),
      profileImage: user.profileImage || null
    };

    const joinMessage = {
      type: 'join_chat',
      chatId: chatId,
      userData: userData
    };

    // Function to send join_chat message
    const sendJoinChat = () => {
      const ws = websocketService.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(joinMessage));
        console.log(`📤 Sent join_chat for chat ${chatId}:`, joinMessage);
        return true;
      } else {
        console.log('⚠️ WebSocket not ready for join_chat:', {
          wsExists: !!ws,
          readyState: ws?.readyState,
          chatId
        });
        return false;
      }
    };

    // Try to send immediately
    if (!sendJoinChat()) {
      // If WebSocket not ready, set up retry mechanism
      const maxRetries = 10;
      let retryCount = 0;
      
      const retryJoin = () => {
        if (retryCount >= maxRetries) {
          console.error('Failed to join chat after max retries');
          return;
        }
        
        if (sendJoinChat()) {
          console.log('Successfully joined chat after retry');
          return;
        }
        
        retryCount++;
        console.log(`Retrying join_chat (${retryCount}/${maxRetries})...`);
        setTimeout(retryJoin, 500);
      };
      
      // Start retry process
      setTimeout(retryJoin, 100);
    }

    // Also listen for WebSocket connection events
    const handleWSConnect = () => {
      console.log('🔗 WebSocket connected, sending join_chat');
      setTimeout(() => sendJoinChat(), 100);
    };

    window.addEventListener('websocket-connected', handleWSConnect);

    // Send periodic activity updates while user is active in chat
    const activityInterval = setInterval(() => {
      const ws = websocketService.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'chat_activity',
          chatId: chatId
        }));
        console.log(`💓 Sent chat_activity heartbeat for chat ${chatId}`);
      }
    }, 30000); // Every 30 seconds instead of 60

    // Cleanup function - send leave_chat when component unmounts or chatId changes
    return () => {
      const ws = websocketService.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        const leaveMessage = {
          type: 'leave_chat',
          chatId: chatId
        };
        ws.send(JSON.stringify(leaveMessage));
        console.log(`📤 Sent leave_chat for chat ${chatId}:`, leaveMessage);
      } else {
        console.log('WebSocket not available for leave_chat:', {
          wsExists: !!ws,
          readyState: ws?.readyState,
          chatId
        });
      }

      clearInterval(activityInterval);
      window.removeEventListener('websocket-connected', handleWSConnect);
    };
  }, [chatId, user?.id, isProjectChat, getUserColor, user]);

  // Send activity update when user interacts with the chat
  const sendActivityUpdate = useCallback(() => {
    if (!chatId || !isProjectChat) return;

    const ws = websocketService.getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'chat_activity',
        chatId: chatId
      }));
    }
  }, [chatId, isProjectChat]);

  // Track user activity (scrolling, typing, etc.)
  useEffect(() => {
    const handleUserActivity = () => {
      sendActivityUpdate();
    };

    const events = ['scroll', 'click', 'keydown', 'mousemove'];
    
    if (chatContainerRef.current) {
      events.forEach(event => {
        chatContainerRef.current.addEventListener(event, handleUserActivity, { passive: true });
      });
    }

    return () => {
      if (chatContainerRef.current) {
        events.forEach(event => {
          chatContainerRef.current.removeEventListener(event, handleUserActivity);
        });
      }
    };
  }, [sendActivityUpdate]);

  // Main message handling
  const {
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    isLoadingChat,
    error,
    setError,
    typingMessage,
    sseStreamingActive,
    handleSend,
    handleReprompt,
    cancelResponse,
    deepResearchProgress,
    handleDiscussionMessage
  } = useMessageHandling({
    chatId,
    navigate,
    selectedModel,
    scrollToBottom,
    onChatCreated: handleChatCreated,
    currentUser: user,
    onTypingEvent: isProjectChat ? handleTypingEvent : undefined,
    projectId: isProjectChat ? projectId : null,
    clientId: isClientChat ? clientId : null,
    toolCallManager: toolCallManager,
    usePagination: true // Enable pagination mode
  });

  // Message pagination
  const {
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
    resetPagination
  } = useMessagePagination({
    chatId,
    setMessages,
    setIsLoading,
    setError
  });

  // Restore artifact panel from localStorage when messages load (e.g. after refresh)
  useEffect(() => {
    if (FORCE_ARTIFACT_PANEL || isLoadingChat || !messages?.length || !chatId) return;
    try {
      const raw = localStorage.getItem(ARTIFACT_PANEL_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved?.isOpen || saved.chatId !== chatId || !saved.messageId) return;
      if (hasRestoredArtifactRef.current) return;
      const artifact = findArtifactInMessages(messages, saved.messageId, chatId);
      if (artifact) {
        hasRestoredArtifactRef.current = true;
        openArtifactPanel(artifact);
      }
    } catch (e) { /* ignore */ }
  }, [chatId, messages, isLoadingChat, FORCE_ARTIFACT_PANEL, openArtifactPanel]);

  // Auto-open newly generated PDFs in side panel (skip initial chat load).
  useEffect(() => {
    if (!messages?.length || !chatId) return;

    let latestPdfArtifact = null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (isPreviewableArtifact(msg?.generatedFile) && (msg.generatedFile.format?.toLowerCase?.() === 'pdf' || msg.generatedFile.fileName?.toLowerCase?.().endsWith('.pdf'))) {
        latestPdfArtifact = { messageId: msg._id, chatId, generatedFile: msg.generatedFile };
        break;
      }
      if (msg?.responses?.length) {
        for (let j = msg.responses.length - 1; j >= 0; j -= 1) {
          const responseFile = msg.responses[j]?.generatedFile;
          if (isPreviewableArtifact(responseFile) && (responseFile.format?.toLowerCase?.() === 'pdf' || responseFile.fileName?.toLowerCase?.().endsWith('.pdf'))) {
            latestPdfArtifact = { messageId: msg._id, chatId, generatedFile: responseFile };
            break;
          }
        }
        if (latestPdfArtifact) break;
      }
    }

    const buildArtifactKey = (artifact) => {
      if (!artifact) return null;
      const generatedFile = artifact.generatedFile;
      return [
        artifact.messageId,
        generatedFile?.s3Key || '',
        generatedFile?.fileName || '',
        generatedFile?.fileSize || ''
      ].join(':');
    };

    if (!hasInitializedPdfAutoOpenRef.current) {
      hasInitializedPdfAutoOpenRef.current = true;
      lastAutoOpenedPdfKeyRef.current = buildArtifactKey(latestPdfArtifact);
      return;
    }

    if (!latestPdfArtifact) return;

    const artifactKey = buildArtifactKey(latestPdfArtifact);

    if (lastAutoOpenedPdfKeyRef.current === artifactKey) return;

    lastAutoOpenedPdfKeyRef.current = artifactKey;
    openArtifactPanel(latestPdfArtifact);
  }, [messages, chatId, openArtifactPanel]);

  // Keep activeArtifact.sentDrafts in sync with live messages so the ArtifactPanel
  // reflects sent-state in real time when an email is sent from the EmailDraftCard.
  useEffect(() => {
    if (!activeArtifact || activeArtifact.type !== 'email' || !activeArtifact.messageId) return;
    const msg = messages?.find(m => m._id === activeArtifact.messageId);
    const liveSentDrafts = msg?.metadata?.sentDrafts;
    if (!liveSentDrafts?.length) return;
    setActiveArtifact(prev => {
      if (!prev) return prev;
      const prevLen = prev.sentDrafts?.length ?? 0;
      if (prevLen === liveSentDrafts.length) return prev;
      return { ...prev, sentDrafts: liveSentDrafts };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, activeArtifact?.messageId, activeArtifact?.type]);

  // Auto-open artifact panel when PPTX code starts streaming via file_gen_code_chunk events.
  // Tracks which message triggered the panel so we only open once per generation.
  const activeCodeStreamRef = useRef(null);
  useEffect(() => {
    if (!messages?.length) return;
    const streamingMsg = messages.find((m) => isPptxCodePreviewMessage(m));
    if (streamingMsg && activeCodeStreamRef.current !== streamingMsg._id) {
      activeCodeStreamRef.current = streamingMsg._id;
      openArtifactPanel({
        type: 'code_streaming',
        messageId: streamingMsg._id,
        chatId,
        codePreview: streamingMsg._codePreview,
        isComplete: false,
        generatedFile: null,
      });
    }
    if (!streamingMsg) activeCodeStreamRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chatId]);

  // Keep the code_streaming artifact panel updated as code arrives and when job completes.
  useEffect(() => {
    if (!activeArtifact || activeArtifact.type !== 'code_streaming' || !activeArtifact.messageId) return;
    const msg = messages?.find(m => m._id === activeArtifact.messageId);
    if (!msg) return;
    // Job completed — attach generatedFile and mark done
    if (msg.generatedFile && !activeArtifact.isComplete) {
      setActiveArtifact(prev => prev ? { ...prev, generatedFile: msg.generatedFile, isComplete: true } : prev);
      return;
    }
    // Code still streaming — update preview (keep last value even after _codePreview is cleared)
    if (msg._codePreview && msg._codePreview !== activeArtifact.codePreview) {
      setActiveArtifact(prev => prev ? { ...prev, codePreview: msg._codePreview } : prev);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, activeArtifact?.messageId, activeArtifact?.type, activeArtifact?.isComplete]);

  // Reset pagination when chat changes
  useEffect(() => {
    if (chatId) {
      resetPagination();
    }
  }, [chatId, resetPagination]);

  // Chat length management with debug mode support
  const debugUrlParams = new URLSearchParams(window.location.search);
  const debugChatLength = debugUrlParams.get('debugChatLength'); // ?debugChatLength=approaching|warning|blocked
  const chatStats = useChatLength(messages, selectedModel?.id || selectedModel, debugChatLength);

  // Calculate total input area height including ChatLengthWarning
  const getTotalInputAreaHeight = useCallback(() => {
    const baseHeight = getEffectiveChatInputHeight();
    
    // Add height for ChatLengthWarning if it's visible
    if (chatStats && chatStats.status !== 'normal') {
      // ChatLengthWarning has pb-3 (12px) + text height (~14px for text-xs)
      // Tighter estimate: ~20px total height when visible
      return baseHeight + 26;
    }
    
    return baseHeight;
  }, [chatStats, chatInputHeight]);

  // Check scroll button visibility (separate from gradient)
  const checkScrollButtonVisibility = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const scrollButtonThreshold = getTotalInputAreaHeight() + 20;
    const tolerance = 5;
    const shouldShowButton = distanceFromBottom > scrollButtonThreshold + tolerance;
    setShowScrollButton(shouldShowButton);
  }, [getTotalInputAreaHeight]);

  // Set up scroll event listener and check initial position (copied from ChatSuggestions.js pattern)
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    // Check initial scroll position using requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      checkScrollPosition();
      checkScrollButtonVisibility();
    });
    
    // Add scroll event listener
    const handleScroll = () => {
      checkScrollPosition();
      checkScrollButtonVisibility();
    };
    
    container.addEventListener('scroll', handleScroll);
    
    // Cleanup
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [checkScrollPosition, checkScrollButtonVisibility]);

  // Check if error indicates chat not found (more comprehensive detection)
  const isChatNotFoundError = error && (
    // Direct "Chat not found" messages
    error.includes('Chat not found') || 
    // 404 errors related to chat
    (error.includes('404') && (error.includes('Chat') || error.includes('chat'))) ||
    // 500 errors with invalid chat ID patterns (from preview endpoint)
    (error.includes('500') && error.includes('Failed to fetch messages')) ||
    // Generic patterns for missing/invalid chats
    error.includes('Failed to load messages: Failed to fetch messages') ||
    // Network/server errors when chat doesn't exist
    (error.includes('Request failed with status code') && (error.includes('404') || error.includes('500')))
  );

  // Handle creating a new chat
  const handleNewChat = useCallback(() => {
    if (isProjectChat && projectId) {
      navigate(`/chat?project=${projectId}`);
    } else if (isClientChat && clientId) {
      navigate(`/?client=${clientId}`);
    } else {
      navigate('/chat');
    }
  }, [navigate, isProjectChat, projectId, isClientChat, clientId]);

  // Check scroll position when messages change (copied from ChatSuggestions.js pattern)
  useEffect(() => {
    if (messages.length > 0) {
      // Use requestAnimationFrame for better timing - ensures DOM is fully rendered
      requestAnimationFrame(() => {
        checkScrollPosition();
        checkScrollButtonVisibility();
        
        // Double-check after a small delay in case content is still loading
        setTimeout(() => {
          checkScrollPosition();
          checkScrollButtonVisibility();
        }, 50);
      });
    }
  }, [messages, checkScrollPosition, checkScrollButtonVisibility]);

  // File upload functionality  
  const { handleFileUpload } = useFileUpload({
    chatId,
    setMessages,
    navigate,
    setError,
    setIsLoading,
    sseStreamingActive,
    currentUser: user
  });

  // Handle search from header
  const handleSearch = useCallback((searchData) => {
    console.log("Search data received:", searchData ? JSON.stringify(searchData) : "null");
    setSearchState(searchData);
  }, []);

  // Simple handlers
  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleFeedbackSubmit = useCallback(() => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    
    setShowFeedbackNotification(true);
    
    feedbackTimeoutRef.current = setTimeout(() => {
      setShowFeedbackNotification(false);
    }, 2500);
  }, []);

  const handleChatInputHeightChange = useCallback((height) => {
    setChatInputHeight(height);
  }, []);

  // Instant local echo for user messages so Enter/send never feels frozen.
  const handleOptimisticSend = useCallback((messageText) => {
    const text = (messageText || '').trim();
    if (!text) return null;

    const tempId = `temp-user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMessage = {
      _id: tempId,
      text,
      sender: 'user',
      timestamp: new Date(),
      isTemporary: true,
      userId: user?.id || user?._id || null
    };

    setMessages((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return [...current, optimisticMessage];
    });
    setIsLoading(true);

    return () => {
      setMessages((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        return current.filter((msg) => msg?._id !== tempId);
      });
      setIsLoading(false);
    };
  }, [setMessages, setIsLoading, user]);

  const getEffectiveChatInputHeight = () => chatInputHeight;

  // Message sending wrapper
  const handleMessageSend = useCallback(async (message, provider, model, toolOptions = {}) => {
    if (!message?.trim()) return;
    
    setInput("");
    
    // If we're currently showing greeting, start transition
    if (showGreeting) {
      setIsTransitioning(true);
      // Small delay to start the transition, then hide greeting
      setTimeout(() => {
        setShowGreeting(false);
      }, 200);
      // Reset transition state quickly so messages appear fast (chat input continues sliding)
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    } else {
      setShowGreeting(false);
    }
    
    await handleSend(
      message,
      provider ?? selectedModel?.provider,
      model ?? selectedModel?.id,
      toolOptions
    );
  }, [handleSend, selectedModel, showGreeting]);

  // Retry functionality - resend the last user message
  const handleRetry = useCallback(async () => {
    // Find the last user message
    const lastUserMessage = messages.filter(msg => msg?.sender === 'user').pop();
    
    if (lastUserMessage?.text && selectedModel?.provider && selectedModel?.id) {
      console.log('Retrying last message:', lastUserMessage.text);
      setError(null); // Clear the error
      await handleSend(lastUserMessage.text, selectedModel.provider, selectedModel.id, {});
    } else {
      console.warn('No user message found to retry');
    }
  }, [messages, handleSend, selectedModel, setError]);

  // Removed auto-scroll when messages change to prevent unwanted scrolling during bot responses
  // Users can manually scroll or use the scroll-to-bottom button

  // Add scroll listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, [checkScrollPosition]);

  // Check scroll position immediately after the first bot response starts and during streaming
  useEffect(() => {
    if (messages.length === 2 && messages[1]?.sender === 'bot') {
      // First bot response - check scroll position using requestAnimationFrame for better timing
      let animationFrameId;
      const checkPositionWithFrame = () => {
        checkScrollPosition();
        animationFrameId = requestAnimationFrame(checkPositionWithFrame);
      };
      
      // Start checking immediately
      animationFrameId = requestAnimationFrame(checkPositionWithFrame);
      
      // Stop checking when streaming is done
      const intervalId = setInterval(() => {
        if (!messages[1]?.isStreaming && !messages[1]?.isThinking) {
          cancelAnimationFrame(animationFrameId);
          clearInterval(intervalId);
        }
      }, 100); // Check if streaming stopped every 100ms
      
      return () => {
        cancelAnimationFrame(animationFrameId);
        clearInterval(intervalId);
      };
    }
  }, [messages.length, messages, checkScrollPosition]);



  // Show greeting for new chats
  useEffect(() => {
    // Only show greeting if we don't have a chatId (truly new chat)
    // Don't show it just because messages haven't loaded yet in an existing chat
    if (!chatId) {
      setShowGreeting(true);
    } else {
      setShowGreeting(false);
    }
  }, [chatId, messages.length]);

  // Immediately show greeting when navigating to new chat
  useEffect(() => {
    if (!chatId) {
      setShowGreeting(true);
    }
  }, [chatId]);

  // Parent component interface
  useImperativeHandle(ref, () => ({
    handleSend: handleMessageSend,
    messages,
    handleFileUpload,
    setSelectedModel,
    isLoading,
    cancelResponse,
    isBotResponding: isLoading,
    activeUsers,
    // Expose typing state for mobile input
    currentTypingUser,
    isProjectChat,
    getTypingState: () => ({
      isOtherUserTyping: currentTypingUser ? true : false,
      typingUserColor: currentTypingUser?.color,
      typingUserName: currentTypingUser?.name,
      isProjectChat
    })
  }));

  // Cleanup
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  // Update parent
  useEffect(() => {
    if (onMessagesUpdate) {
      onMessagesUpdate(messages);
    }
  }, [messages]);

  useEffect(() => {
    const next = parseDefaultChatModelKey(user?.preferences?.defaultChatModelKey);
    if (!next) return;
    setSelectedModel((prev) => (prev == null ? next : prev));
  }, [user?.preferences?.defaultChatModelKey]);

  // Report model selection changes to parent (for header sync)
  useEffect(() => {
    onModelSelectionChange?.(selectedModel);
  }, [selectedModel, onModelSelectionChange]);

  // Keyboard shortcut for new chat (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        // Only allow new chat creation if not in a project chat
        if (!isProjectChat) {
          event.preventDefault();
          navigate('/chat');
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, isProjectChat]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#111111]">
      {/* User join notifications */}
      {joinNotifications.map((notification) => (
        <div
          key={notification.id}
          className={`fixed top-24 left-0 right-0 flex justify-center z-50 transition-all duration-300 ease-in-out transform opacity-100 translate-y-0`}
          style={{ top: `${96 + (joinNotifications.indexOf(notification) * 60)}px` }}
        >
          <div 
            className="border border-gray-300 dark:border-[#181818] rounded-[12px] px-4 py-2 flex items-center gap-2 shadow-sm"
            style={{ 
              backgroundColor: notification.userColor,
              color: '#000000'
            }}
          >
            <span className="text-sm font-medium">{notification.firstName} is here.</span>
            <button 
              onClick={() => closeJoinNotification(notification.id)}
              className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors duration-150"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      {/* Feedback notification - portaled to body, z above header (60), centered over chat area */}
      {showFeedbackNotification && createPortal(
        <div 
          className="fixed top-4 flex justify-center z-[100] transition-opacity duration-300 ease-in-out"
          style={{
            left: chatAreaBounds ? chatAreaBounds.left + chatAreaBounds.width / 2 : '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <div 
            className="feedback-notification-squircle px-[14px] py-2 flex items-center gap-2 border"
            style={{
              background: darkMode ? '#252525' : '#ebebeb',
              color: darkMode ? '#999999' : '#525252',
              borderColor: darkMode ? '#333' : '#d4d4d4',
              fontSize: 13,
            }}
          >
            <CelebrateIcon className="flex-shrink-0 w-4 h-4" />
            <span>Thanks for your feedback!</span>
            <button 
              onClick={() => setShowFeedbackNotification(false)}
              className="p-1 hover:opacity-70 rounded-full transition-opacity duration-150"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Main layout wrapper */}
      <div className="flex flex-col h-full">
        {/* 🧪 TEMPORARY: Test component for unified loading system - HIDDEN */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="p-4">
            <TestUnifiedLoading />
          </div>
        )} */}
        
        {/* Main content area - flex row: [chat area + input | artifact panel] */}
        <div className="flex-1 overflow-hidden relative flex min-w-0">
          {/* Chat area - shrinks when artifact panel is open, contains messages + input */}
          <div ref={chatAreaRef} className="relative flex-1 min-w-0 overflow-hidden flex flex-col">
          {/* Greeting + Chat Input centered together */}
          {showGreeting && (
            <div className={`absolute inset-0 flex flex-col items-center justify-center px-4 transition-all duration-1200 ease-out ${
              isTransitioning ? 'opacity-0 transform -translate-y-8' : 'opacity-100 transform translate-y-0'
            }`}>
              <div className="w-full max-w-[800px] flex flex-col items-center gap-8 md:gap-12 -mt-8 md:-mt-10">
                {/* Greeting component */}
                <div className="relative w-full">
                  <GreetingComponentV3 
                    user={user}
                    onExampleClick={handleMessageSend}
                  />
                </div>
                
                {/* Chat Input when greeting is shown */}
                <div className="w-full">
                  <ChatInput
                    input={input}
                    setInput={setInput}
                    handleSend={handleMessageSend}
                    onOptimisticSend={handleOptimisticSend}
                    handleDiscussionSend={isProjectChat ? handleDiscussionMessage : undefined}
                    disabled={isLoading || chatStats?.isBlocked}
                    isInitialChatLoad={isLoadingChat}
                    companyName={user?.company?.name || "your company"}
                    organizationNickname={user?.company?.nickname}
                    handleFileUpload={handleFileUpload}
                    onModelSelect={(provider, model) => setSelectedModel({ provider, id: model })}
                    selectedModelFromParent={selectedModel}
                    cancelResponse={cancelResponse}
                    onHeightChange={handleChatInputHeightChange}
                    error={isChatNotFoundError ? null : error}
                    onErrorRetry={handleRetry}
                    isOtherUserTyping={currentTypingUser ? true : false}
                    typingUserColor={currentTypingUser?.color}
                    typingUserName={currentTypingUser?.name}
                    isProjectChat={isProjectChat}
                    hasMessages={messages.length > 0}
                    user={user}
                    setUser={setUser}
                    chatId={chatId}
                    projectId={projectId}
                    projectData={projectData}
                  />
                  <ChatLengthWarning 
                    chatStats={chatStats}
                    onCreateNewChat={handleNewChat}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Messages area - hidden when showing greeting */}
          {!showGreeting && (
            <div className="relative flex-1 min-h-0 flex flex-col">
              {/* Fixed gradient at top when you can scroll up */}
              {canScrollUp && (
                <div className="absolute top-0 left-0 right-0 h-14 bg-gradient-to-b from-white via-white/90 via-white/70 to-transparent dark:from-[#111111] dark:via-[#111111]/90 dark:via-[#111111]/70 dark:to-transparent pointer-events-none z-50" />
              )}

              {/* Scroll to bottom button - spring animation like mobile (scale 0↔1) */}
              <div
                className="absolute left-0 right-0 flex justify-center z-10"
                style={{
                  bottom: `${typeof window !== 'undefined' && window.innerWidth < 768 ? 24 : 12}px`,
                  pointerEvents: showScrollButton ? 'auto' : 'none',
                }}
              >
                <div
                  className="group relative inline-block pointer-events-auto origin-center"
                  style={{
                    transform: showScrollButton ? 'scale(1)' : 'scale(0)',
                    opacity: showScrollButton ? 1 : 0,
                    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out',
                  }}
                >
                  <button
                    onClick={scrollToBottom}
                    className="flex items-center rounded-full bg-[#f5f5f5] dark:bg-[#181818] border border-[#d9d9d9] dark:border-[#282828] shadow-sm hover:bg-[#e8e8e8] dark:hover:bg-[#1f1f1f] transition-colors duration-200 overflow-hidden"
                    aria-label="Scroll to bottom"
                  >
                    <div className="p-2">
                      <FaArrowUp className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 rotate-180" />
                    </div>
                  </button>
                </div>
              </div>
              
              {/* was: chat-container-query on this div + @container chat rules in chat.css for .chat-logo-avatar */}
              <div 
                ref={chatContainerRef}
                className={`chat-container h-full overflow-y-auto transition-all duration-500 ease-out ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}
                style={{
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* IE and Edge */
                  WebkitScrollbarWidth: 'none'
                }}
                onMouseEnter={(e) => {
                  // Show scrollbar on hover
                  e.currentTarget.style.scrollbarWidth = 'thin';
                  e.currentTarget.style.scrollbarColor = darkMode ? '#181818 transparent' : '#E5E7EB transparent';
                }}
                onMouseLeave={(e) => {
                  // Hide scrollbar when not hovering
                  e.currentTarget.style.scrollbarWidth = 'none';
                  e.currentTarget.style.scrollbarColor = 'transparent transparent';
                }}
              >
                {/* Show error when chat not found */}
                {isChatNotFoundError && !isLoading && (
                  <ChatNotFoundError 
                    darkMode={darkMode} 
                    onNewChat={handleNewChat}
                  />
                )}

                {/* Show loader when loading initial chat */}
                {isLoading && messages.length === 0 && !isChatNotFoundError && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <NvgtrShimmerLoader darkMode={darkMode} size={50} />
                  </div>
                )}
                
                {/* Messages */}
                {messages.length > 0 && !isChatNotFoundError && (
                  <div className="animate-fade-in">
                    <ChatMessages
                    messages={Array.isArray(messages) ? messages : []}
                    setMessages={setMessages}
                    chatId={chatId}
                    chatTitle={getCachedChatTitle(chatId) || 'Untitled Chat'}
                    typingMessage={typingMessage}
                    isLoading={isLoading}
                    showGreeting={false}
                    handleCopy={handleCopy}
                    handleEdit={() => {}} // Removed editing
                    handleReprompt={handleReprompt}
                    handleFeedbackSubmit={handleFeedbackSubmit}
                    editingMessageIndex={null}
                    setEditingMessageIndex={() => {}}
                    editInput=""
                    setEditInput={() => {}}
                    showScrollbar={false}
                    searchState={searchState}
                    chatInputHeight={getTotalInputAreaHeight()}
                    handleChatInputHeightChange={handleChatInputHeightChange}
                    projectData={projectData}
                    currentUser={user}
                    toolCallManager={toolCallManager}
                    onLoadMore={loadMoreMessages}
                    hasMoreMessages={hasMoreMessages}
                    isLoadingMore={isLoadingMore}
                    chatContainerRef={chatContainerRef}
                    onOpenArtifact={openArtifactPanel}
                  />
                </div>
              )}
              </div>
            </div>
          )}

          {/* Chat input - inside chat area so it slides over with messages when artifact panel opens */}
          {!showGreeting && !isChatNotFoundError && (
            <div className="relative flex-shrink-0">
              <ChatInput
                input={input}
                setInput={setInput}
                handleSend={handleMessageSend}
                onOptimisticSend={handleOptimisticSend}
                handleDiscussionSend={isProjectChat ? handleDiscussionMessage : undefined}
                disabled={isLoading || chatStats?.isBlocked}
                isInitialChatLoad={isLoadingChat}
                companyName={user?.company?.name || "your company"}
                organizationNickname={user?.company?.nickname}
                handleFileUpload={handleFileUpload}
                onModelSelect={(provider, model) => setSelectedModel({ provider, id: model })}
                selectedModelFromParent={selectedModel}
                cancelResponse={cancelResponse}
                onHeightChange={handleChatInputHeightChange}
                error={isChatNotFoundError ? null : error}
                onErrorRetry={handleRetry}
                isOtherUserTyping={currentTypingUser ? true : false}
                typingUserColor={currentTypingUser?.color}
                typingUserName={currentTypingUser?.name}
                isProjectChat={isProjectChat}
                hasMessages={messages.length > 0}
                user={user}
                setUser={setUser}
                chatId={chatId}
                projectId={projectId}
                projectData={projectData}
              />
              <ChatLengthWarning 
                chatStats={chatStats}
                onCreateNewChat={handleNewChat}
              />
            </div>
          )}
          </div>

          {/* Artifact panel - slides out from right when chart/file artifact exists (not rendered when lifted to MainInterface) */}
          {activeArtifact && !artifactPanelLifted && (
            <ArtifactPanel
              isOpen={isArtifactPanelOpen}
              onOpen={() => setIsArtifactPanelOpen(true)}
              onClose={closeArtifactPanel}
              artifact={activeArtifact}
              chatId={chatId}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default Chat;

